import React, { useState } from 'react';
import { useEditorStore } from '../store/editorStore';
import { generateShareLink, downloadProject, generateEmbedCode } from '../utils/share';
import { Share, Download, Play } from 'lucide-react';

const Toolbar: React.FC = () => {
  const { project, addExternalLibrary, externalLibraries, incrementPreviewKey } = useEditorStore();
  const [showShareModal, setShowShareModal] = useState(false);
  const [showLibModal, setShowLibModal] = useState(false);
  const [newLibUrl, setNewLibUrl] = useState('');
  const [embedCode, setEmbedCode] = useState('');

  const handleShare = () => {
    const shareLink = generateShareLink(project);
    navigator.clipboard.writeText(shareLink);
    alert('分享链接已复制到剪贴板！');
  };

  const handleDownload = () => {
    downloadProject(project);
  };

  const handleAddLibrary = () => {
    if (newLibUrl.trim()) {
      addExternalLibrary(newLibUrl);
      setNewLibUrl('');
    }
  };

  const handleGenerateEmbed = () => {
    const code = generateEmbedCode(project, 800, 600);
    setEmbedCode(code);
    navigator.clipboard.writeText(code);
    alert('嵌入代码已复制到剪贴板！');
  };

  const handleRun = () => {
    incrementPreviewKey();
  };

  return (
    <div className="bg-editor-panel border-b border-editor-border px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <h1 className="text-lg font-bold text-gray-200">在线代码编辑器</h1>
        <span className="text-sm text-gray-400">- {project.name}</span>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={handleRun}
          className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
          title="运行代码"
        >
          <Play size={16} />
          运行
        </button>

        <button
          onClick={() => setShowLibModal(true)}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
          title="管理外部库"
        >
          外部库 ({externalLibraries.length})
        </button>

        <button
          onClick={() => setShowShareModal(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded transition-colors"
          title="分享项目"
        >
          <Share size={16} />
          分享
        </button>

        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded transition-colors"
          title="下载项目"
        >
          <Download size={16} />
          下载
        </button>
      </div>

      {/* 分享模态框 */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-editor-panel rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-200 mb-4">分享项目</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">分享链接</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={generateShareLink(project)}
                    readOnly
                    className="flex-1 px-3 py-2 bg-editor-bg border border-editor-border rounded text-sm text-gray-300"
                  />
                  <button
                    onClick={handleShare}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                  >
                    复制
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">嵌入代码</label>
                <textarea
                  value={embedCode}
                  onChange={(e) => setEmbedCode(e.target.value)}
                  className="w-full h-24 px-3 py-2 bg-editor-bg border border-editor-border rounded text-sm text-gray-300 font-mono"
                  placeholder="点击生成嵌入代码..."
                />
                <button
                  onClick={handleGenerateEmbed}
                  className="mt-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm w-full"
                >
                  生成并复制嵌入代码
                </button>
              </div>
            </div>

            <button
              onClick={() => {
                setShowShareModal(false);
                setEmbedCode('');
              }}
              className="mt-6 w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm"
            >
              关闭
            </button>
          </div>
        </div>
      )}

      {/* 外部库模态框 */}
      {showLibModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-editor-panel rounded-lg p-6 max-w-2xl w-full">
            <h2 className="text-xl font-bold text-gray-200 mb-4">管理外部库</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">添加外部库 (CDN URL)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newLibUrl}
                    onChange={(e) => setNewLibUrl(e.target.value)}
                    placeholder="https://cdn.example.com/library.js"
                    className="flex-1 px-3 py-2 bg-editor-bg border border-editor-border rounded text-sm text-gray-300"
                  />
                  <button
                    onClick={handleAddLibrary}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                  >
                    添加
                  </button>
                </div>
              </div>

              <div>
                <h3 className="text-sm text-gray-400 mb-2">已添加的库</h3>
                <div className="space-y-2 max-h-60 overflow-auto">
                  {externalLibraries.map((url) => (
                    <div key={url} className="flex items-center justify-between p-2 bg-editor-bg rounded">
                      <span className="text-sm text-gray-300 truncate">{url}</span>
                      <button
                        onClick={() => {
                          useEditorStore.getState().removeExternalLibrary(url);
                        }}
                        className="text-red-500 hover:text-red-400 text-sm"
                      >
                        删除
                      </button>
                    </div>
                  ))}
                  {externalLibraries.length === 0 && (
                    <div className="text-sm text-gray-500 text-center py-4">
                      尚未添加任何外部库
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 p-3 bg-blue-900 bg-opacity-30 rounded border border-blue-700">
                <h4 className="text-sm font-semibold text-blue-300 mb-2">常用库示例：</h4>
                <ul className="text-xs text-blue-200 space-y-1">
                  <li>React: https://unpkg.com/react@18/umd/react.production.min.js</li>
                  <li>Vue: https://unpkg.com/vue@3/dist/vue.global.js</li>
                  <li>Lodash: https://cdn.jsdelivr.net/npm/lodash@4.17.21/lodash.min.js</li>
                  <li>Axios: https://cdn.jsdelivr.net/npm/axios@1.6.0/dist/axios.min.js</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => {
                setShowLibModal(false);
                setNewLibUrl('');
              }}
              className="mt-6 w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm"
            >
              关闭
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Toolbar;
